import { ListChecks, FolderKanban, Bell, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCopy } from '../../i18n';
import { useUIState } from '../../contexts/UIStateContext';

/**
 * Navegação primária do celular.
 *
 * Substitui a gaveta, que era o defeito estrutural do app no telefone: ela
 * ocupava 260 de ~375px e não era navegação, era um explorador de projetos com
 * favoritos, pastas, escopos e organização. Num monitor isso convive ao lado do
 * conteúdo; num telefone é 70% da tela para escolher onde trabalhar antes de
 * ver qualquer trabalho.
 *
 * Quatro destinos, e o número importa: com cinco ou mais, o alvo de toque cai
 * abaixo do confortável em 375px, e a barra vira menu. Cada aba responde uma
 * pergunta diferente:
 *
 *   Hoje     o que é meu agora (abre o app; atravessa projetos)
 *   Tarefas       o que é meu, de todos os projetos
 *   Projetos      onde eu trabalho; daqui se chega ao Quadro
 *   Avisos        o que aconteceu enquanto eu estava fora
 *   Ajustes       conta, preferências, sair
 *
 * Fica escondida acima de `md`: no desktop a sidebar continua sendo a navegação,
 * e ela é boa lá.
 */

/**
 * Todo destino aqui e AUTOSSUFICIENTE: abre sem depender de escolha feita em
 * outro lugar.
 *
 * Foi por violar isso que a aba "Quadro" saiu. Ela precisava de um projeto
 * corrente que a barra nao tem como garantir, entao as vezes era tela e as
 * vezes era pergunta. O Quadro nao sumiu do app: ele voltou a ser o que sempre
 * foi, o lugar aonde se chega DEPOIS de escolher o projeto, em Projetos.
 */
const DESTINOS = [
  { id: 'tarefas', icone: ListChecks },
  { id: 'projetos', icone: FolderKanban },
  { id: 'notificacoes', icone: Bell },
  { id: 'ajustes', icone: Settings },
] as const;

interface MobileTabBarProps {
  /** Abas sem módulo próprio (Projetos, Mais) são telas do shell. */
  telaDoShell: string | null;
  onTelaDoShell: (_tela: string | null) => void;
}

const MobileTabBar = ({ telaDoShell, onTelaDoShell }: MobileTabBarProps) => {
  const t = useCopy().common.mobileTabs;
  const { currentModule, changeModule } = useUIState();

  const ativo = telaDoShell ?? (currentModule === 'hoje' ? 'tarefas' : currentModule);

  // 'tarefas' e modulo (tem rota e link profundo); os outros tres sao telas do
  // shell, porque nao pertencem a projeto nenhum e nao precisam de endereco.
  const ir = (id: string) => {
    if (id === 'tarefas') {
      onTelaDoShell(null);
      changeModule('hoje');
      return;
    }
    onTelaDoShell(id);
  };

  return (
    <nav
      data-testid="mobile-tabbar"
      aria-label={t.aria}
      className={cn(
        'hidden max-md:grid grid-cols-4 shrink-0',
        'border-t border-[var(--color-border)] bg-[var(--color-bg)]',
        // A barra é o último elemento alcançável do polegar: sem o inset, a
        // barra de gestos do iPhone come os rótulos.
        'pb-[env(safe-area-inset-bottom)]'
      )}
    >
      {DESTINOS.map(({ id, icone: Icone }) => {
        const selecionado = ativo === id;
        return (
          <button
            key={id}
            data-testid={`tab-mobile-${id}`}
            aria-current={selecionado ? 'page' : undefined}
            onClick={() => ir(id)}
            className={cn(
              'relative flex flex-col items-center gap-1 pt-2.5 pb-3',
              'text-[var(--color-text-muted)] transition-colors',
              selecionado && 'text-[var(--color-primary)]'
            )}
          >
            {/* Traço no topo, e não fundo preenchido: a aba ativa precisa se
                distinguir sem competir com o conteúdo logo acima dela. */}
            {selecionado && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-7 rounded-b bg-[var(--color-primary)]" />
            )}
            <Icone size={22} />
            <span className="text-[11px] font-semibold">{t[id]}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default MobileTabBar;
